import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";
import { RewindIcon, WarningCircleIcon, CheckCircleIcon } from "@phosphor-icons/react";

const Stat = ({ label, value, tone = "default" }) => {
  const toneMap = {
    default: "text-zinc-900",
    good: "text-emerald-700",
    warn: "text-amber-700",
    bad: "text-red-700",
    muted: "text-zinc-500",
  };
  return (
    <div className="rounded-sm border border-zinc-200 bg-white p-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <p className={`mt-1 font-display text-2xl font-black tabular-nums ${toneMap[tone]}`}>
        {value}
      </p>
    </div>
  );
};

export const BackfillDialog = ({ open, onOpenChange, post, connId }) => {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [onlyDm, setOnlyDm] = useState(false);

  const reset = () => {
    setPlan(null);
    setResult(null);
    setOnlyDm(false);
  };

  const loadPreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const p = await api.backfillMedia(connId, post.id, { dryRun: true });
      setPlan(p);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const runLive = async () => {
    if (!window.confirm("This will send real replies + DMs to Instagram. Continue?")) return;
    setRunning(true);
    try {
      const res = await api.backfillMedia(connId, post.id, { dryRun: false, onlyDm });
      setResult(res);
      toast.success(`Backfill complete — ${res.replied} replies, ${res.dm_sent} DMs`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backfill failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="rounded-sm sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight">
            Backfill comments
          </DialogTitle>
          <DialogDescription>
            Process old comments on this post that haven't triggered a reply or DM yet.
            Meta limits: replies only within 7 days, DMs only within 24 hours.
          </DialogDescription>
        </DialogHeader>

        {!plan && !loading && (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <RewindIcon size={48} weight="duotone" className="text-amber-500" />
            <p className="text-sm text-zinc-600">
              We'll fetch up to the 250 most recent comments on this post, skip any we've
              already processed, and show you a preview before sending anything.
            </p>
            <button
              onClick={loadPreview}
              data-testid="backfill-preview-btn"
              className="rounded-sm bg-amber-500 px-6 py-2.5 text-sm font-bold text-zinc-950 hover:bg-amber-600"
            >
              Load preview
            </button>
          </div>
        )}

        {loading && (
          <div className="py-8 text-center text-sm text-zinc-500">Loading comments…</div>
        )}

        {plan && !result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="Total" value={plan.total} />
              <Stat label="Already done" value={plan.already_processed} tone="muted" />
              <Stat label="No match" value={plan.no_match} tone="muted" />
              <Stat
                label="Will process"
                value={plan.actionable + plan.actionable_dm_only}
                tone={plan.actionable + plan.actionable_dm_only > 0 ? "good" : "muted"}
              />
              <Stat label="DM window OK" value={plan.actionable} tone="good" />
              <Stat
                label="Reply-only"
                value={plan.actionable_dm_only}
                tone={plan.actionable_dm_only ? "warn" : "muted"}
              />
              <Stat
                label="Too old (7d+)"
                value={plan.too_old_for_reply}
                tone={plan.too_old_for_reply ? "bad" : "muted"}
              />
              <Stat label="Outside DM" value={plan.outside_dm_window} tone="warn" />
            </div>

            {plan.samples.length > 0 && (
              <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Sample matched comments
                </p>
                <ul className="space-y-2 text-xs">
                  {plan.samples.map((s) => (
                    <li key={s.id} className="flex items-start gap-2">
                      <span className="shrink-0 font-mono text-zinc-400">
                        {s.age_hours}h
                      </span>
                      <span className="font-mono text-zinc-500">@{s.username || "?"}</span>
                      <span className="flex-1 truncate text-zinc-900">{s.text}</span>
                      <span
                        className={`rounded-sm px-1.5 text-[9px] font-bold uppercase ${
                          s.will_dm
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {s.will_dm ? "reply+dm" : "reply only"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <label className="flex items-center gap-2 rounded-sm border border-zinc-200 bg-white px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={onlyDm}
                onChange={(e) => setOnlyDm(e.target.checked)}
                className="h-4 w-4 accent-amber-500"
                data-testid="backfill-only-dm"
              />
              <span className="text-zinc-900">Skip public replies — only send DMs</span>
            </label>

            <DialogFooter>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-sm border border-zinc-200 bg-white px-6 py-2.5 text-sm font-semibold text-zinc-900 hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={running || plan.actionable + plan.actionable_dm_only === 0}
                onClick={runLive}
                data-testid="backfill-run-btn"
                className="rounded-sm bg-amber-500 px-6 py-2.5 text-sm font-bold text-zinc-950 hover:bg-amber-600 disabled:opacity-50"
              >
                {running ? "Running…" : `Send to ${plan.actionable + plan.actionable_dm_only} comments`}
              </button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-sm border border-emerald-200 bg-emerald-50 p-4">
              <CheckCircleIcon size={28} weight="fill" className="text-emerald-600" />
              <div>
                <p className="font-display text-lg font-bold text-emerald-900">Backfill complete</p>
                <p className="text-sm text-emerald-800">
                  {result.replied} public replies, {result.dm_sent} DMs sent. See the Activity
                  tab for full logs (BACKFILL badge).
                </p>
              </div>
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="rounded-sm border border-red-200 bg-red-50 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <WarningCircleIcon size={16} weight="fill" className="text-red-600" />
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-700">
                    First {result.errors.length} errors
                  </p>
                </div>
                <ul className="space-y-1 font-mono text-[11px] text-red-800">
                  {result.errors.map((e, i) => (
                    <li key={i} className="truncate">
                      [{e.step}] {e.comment_id}: {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-sm bg-zinc-950 px-6 py-2.5 text-sm font-bold text-white hover:bg-zinc-800"
              >
                Close
              </button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
